import React from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSelector } from 'react-redux';
import {
	Container,
	Typography,
	Button,
	Card,
	CardHeader,
	CardContent,
	CardActions,
	CircularProgress,
} from '@mui/material';
import { useGetAdminConsentUrlQuery } from '@/features/tenantConsent/tenantConsentApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';

const AdminConsentPage = () => {
	const { account } = useSelector((state) => state.msalAccount);
	const firebaseReady = useSelector((state) => state.firebase.firebaseReady);
	const email = account?.username;
	const {
		isSuccess: isUserDetailsReady,
		isLoading: isUserDetailsLoading,
		isFetching: isUserDetailsFetching,
		isError: isUserDetailsError,
	} = useGetUserDetailsQuery({ email }, { skip: !email || !firebaseReady });

	const { data, isLoading, isFetching, isError } = useGetAdminConsentUrlQuery(
		isUserDetailsReady ? undefined : skipToken,
	);

	const adminConsentUrl = data ?? '';
	const waitingForUserDetails =
		email && firebaseReady && (isUserDetailsLoading || isUserDetailsFetching);
	const isLoadingConsentUrl = waitingForUserDetails || (isUserDetailsReady && (isLoading || isFetching));
	const consentUrlError = isUserDetailsError
		? 'Unable to load user details for admin consent. Please refresh and try again.'
		: isError
			? 'Unable to start Microsoft admin consent. Please try again.'
			: '';

	return (
		<Container maxWidth='sm' sx={{ mt: 10, textAlign: 'center' }}>
			<Card>
				<CardHeader title='Grant Admin Consent' />
				<CardContent>
					<Typography>
						Folio needs admin consent to access Microsoft 365 resources on behalf of
						your organisation. Please click below and sign in as a Global
						Administrator.
					</Typography>
					{consentUrlError && (
						<Typography sx={{ mt: 2 }} color='error'>
							{consentUrlError}
						</Typography>
					)}
				</CardContent>
				<CardActions
					sx={{
						justifyContent: 'center',
						p: 2,
					}}
				>
					<Button
						size='large'
						color='primary'
						variant='contained'
						href={adminConsentUrl || undefined}
						disabled={isLoadingConsentUrl || !adminConsentUrl}
					>
						{isLoadingConsentUrl ? (
							<CircularProgress size={20} color='inherit' />
						) : (
							'Grant Access as Admin'
						)}
					</Button>
				</CardActions>
			</Card>
		</Container>
	);
};

export default AdminConsentPage;
